import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HorizonButtonComponent } from '@horizon/core-buttons';
import { TranslateService } from '@ngx-translate/core';
import { RoutePaths } from '@shared/enums/routes.enums';
import { CallType, GenesysData } from '@shared/interfaces/CallLog';
import { ApiExceptionsHandlerService } from '@shared/services/api-exceptions-handler.service';
import { HorizonGenesysCallLogService } from '@shared/services/horizon-genesys-call-log.service';
import { StorageApiService } from '@shared/services/storage-api.service';
import { TabControllerService } from '@shared/services/tab-controller.service';
import {
  BeforeCloseEventArgs,
  OpenEventArgs,
} from '@syncfusion/ej2-angular-popups';
import {
  catchError,
  filter,
  finalize,
  Observable,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { globalSettings } from 'src/global-settings';

@Component({
  selector: 'horizon-genesys-temporal-substitute',
  templateUrl: './horizon-genesys-temporal-substitute.component.html',
  styleUrls: ['./horizon-genesys-temporal-substitute.component.scss'],
})
export class HorizonGenesysTemporalSubstituteComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Output() public closeSubstitute = new EventEmitter();
  @ViewChild('saveButton')
  public confirmButton: HorizonButtonComponent;

  private callId: string;
  public globalSettings: any = globalSettings;
  private phoneNumber: string;
  private callType: string;
  private callSubType: string;
  public callTypeCode: string;
  public isSubTypeCodeEnabled: boolean = false;
  public form: FormGroup;
  public invalidCallId: boolean = false;

  public callTypeOptions = [
    { key: 'Inbound', caption: 'Inbound' },
    { key: 'Outbound', caption: 'Outbound' },
  ];
  public callTypeOptionsValues = {
    text: 'caption',
    value: 'key',
  };
  public callSubTypeOptions = [
    { key: 'Dialler', caption: 'Dialler' },
    { key: 'Agent', caption: 'Agent' },
  ];
  public callSubTypeOptionsValues = {
    text: 'caption',
    value: 'key',
  };
  private genesysData: GenesysData = {
    callID: '',
    phoneNumber: '',
    callType: null,
    callSubType: null,
  };

  constructor(
    public translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly callLogService: HorizonGenesysCallLogService,
    private readonly tabControllerService: TabControllerService,
    private readonly storageApiService: StorageApiService,
    private readonly apiExceptionHandler: ApiExceptionsHandlerService
  ) {
    this.form = this.fb.group({
      callId: ['', Validators.required],
      phoneNumber: [
        '',
        [Validators.required, , Validators.pattern(/^\+?[0-9\s\-()]{7,20}$/)],
      ],
      callType: ['', Validators.required],
      callSubType: [null],
    });
  }

  public onBeforeClose(event: BeforeCloseEventArgs): void {
    this.callLogService.setGenesysSubstituteIcon('default');
    this.closeSubstitute.emit();
  }

  public onCallIdChange(event: string): void {
    const parsedCallId = this.formatCallId(event);
    this.callId = parsedCallId;
  }

  public onCallTypeChange(event: any): void {
    this.form.controls['callSubType'].setValue(undefined);
    if (event.itemData.key === 'Outbound') {
      this.form.controls['callSubType'].setValidators(Validators.required);
      this.form.controls['callSubType'].enable();
      this.isSubTypeCodeEnabled = true;
    } else {
      this.form.controls['callSubType'].clearValidators;
      this.form.controls['callSubType'].disable();
      this.isSubTypeCodeEnabled = false;
    }
    this.callType = event.itemData.key;
  }

  public onCallSubTypeChange(event: any): void {
    this.callSubType = event.itemData.key;
  }

  public onOpen(args: OpenEventArgs): void {
    args.preventFocus = true;
  }

  public onPhoneNumberChange(event: string): void {
    this.phoneNumber = event;
  }

  public startManualCallLog(): void {
    this.invalidCallId = false;

    this.genesysData.callID = this.callId;
    this.genesysData.phoneNumber = this.phoneNumber;
    this.genesysData.callType = CallType[this.callType];
    this.genesysData.callSubType =
      (this.callSubType ?? '') !== '' ? CallType[this.callSubType] : null;
    this.tabController();
  }

  private tabController(): void {
    this.storageApiService.setLocalStorageData('genesysData', this.genesysData);
    if (!this.tabControllerService.isTabOpen('manualGeneratedTab'))
      this.tabControllerService.openTab(
        'manualGeneratedTab',
        RoutePaths.SEARCH
      );
    this.closeSubstitute.emit();
  }

  private formatCallId(callId: string): string {
    if (!callId || callId === '') return '';
    const parts = callId?.replace(/\/$/, '').split('/');
    return parts[parts.length - 1];
  }

  public checkConversationIdIsValid(): void {
    this.callLogService
      .getConversationDetails(this.callId)
      .pipe(
        takeUntil(this.destroy$),
        filter((conversationDetails) => !!conversationDetails),
        tap(() => this.startManualCallLog()),
        catchError((error: HttpErrorResponse) =>
          this.conversationIdIsInvalid(error)
        ),
        finalize(() => this.confirmButton.stopLoading())
      )
      .subscribe();
  }

  conversationIdIsInvalid(error: HttpErrorResponse): Observable<never> {
    this.invalidCallId = true;
    return this.apiExceptionHandler.catchErrorHandler(error, 'call-log');
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
